import { PayloadAction } from "@reduxjs/toolkit";
import { CreateAssetResponse } from "app/components/MediaUploader/types";
import { set } from "lodash";
import { createSlice } from "utils/@reduxjs/toolkit";
import { GroupByTypeEnum, ISelectedTickets } from "../../../types";
import {
  IAddTaskButton,
  IGroupJsonPayload,
  ITickets,
  TicketGroup,
} from "../types";
import { defaultTicketFormDetail, initialState } from "./initialState";

function updateGroups(
  groups: TicketGroup[],
  idSet: Set<string>,
  updates: Partial<ITickets>,
): TicketGroup[] {
  if (!groups?.length) return groups ?? [];

  return groups.map((group) => ({
    ...group,
    ticketsList: (group.ticketsList ?? []).map((ticket) =>
      idSet.has(ticket._id) ? { ...ticket, ...updates } : ticket,
    ),
    subGroups: updateGroups(group.subGroups ?? [], idSet, updates),
  }));
}

function toggleTagsInGroups(
  groups: TicketGroup[],
  idSet: Set<string>,
  tagIds: string[],
): TicketGroup[] {
  if (!groups?.length) return groups ?? [];

  return groups.map((group) => ({
    ...group,
    ticketsList: (group.ticketsList ?? []).map((ticket) => {
      if (!idSet.has(ticket._id)) return ticket;

      const currentTags: string[] = ticket.tags ?? [];

      // Same toggle logic as setTicketTagsFormValue
      const allAlreadySelected = tagIds.every((id) => currentTags.includes(id));

      const updatedTags = allAlreadySelected
        ? currentTags.filter((id) => !tagIds.includes(id)) // pop
        : [
            // push
            ...currentTags,
            ...tagIds.filter((id) => !currentTags.includes(id)),
          ];

      return { ...ticket, tags: updatedTags };
    }),
    subGroups: toggleTagsInGroups(group.subGroups ?? [], idSet, tagIds),
  }));
}

function matchesGroupId(
  groupId: string | string[],
  targetIds: (string | string[])[],
): boolean {
  return targetIds.some((id) => {
    if (Array.isArray(id) && Array.isArray(groupId)) {
      return (
        JSON.stringify([...id].sort()) === JSON.stringify([...groupId].sort())
      );
    }
    return id === groupId;
  });
}
const formSlice = createSlice({
  name: "ticketGroupsState",
  initialState,
  reducers: {
    toggleCreateTicketIntoGroup: (
      state,
      action: PayloadAction<IAddTaskButton>,
    ) => {
      state.showCreateTicketIntoGroup = action.payload;
    },

    toggleSubgroupCollapsed: (
      state,
      action: PayloadAction<{ groupId: string; isCollapsed: boolean }>,
    ) => {
      const { groupId, isCollapsed } = action.payload;
      state.isSubgroupCollapsed[groupId] = isCollapsed;
    },

    setGroups: (state, action: PayloadAction<TicketGroup[]>) => {
      state.groups = action.payload;
    },

    updateGroupsVisibility: (
      state,
      action: PayloadAction<{
        groupIds: (string | string[])[];
        isVisible: boolean;
      }>,
    ) => {
      const { groupIds, isVisible } = action.payload;

      state.groups = state.groups.map((group) =>
        matchesGroupId(group._id as string | string[], groupIds)
          ? { ...group, isVisible }
          : group,
      );
    },
    updateGroupActions: (state, action: PayloadAction<TicketGroup>) => {
      const updatedGroup = action.payload;

      const updateGroup = (groups: TicketGroup[]) => {
        return groups.map((group) => {
          if (group._id === updatedGroup._id) {
            return { ...group, ...updatedGroup };
          }

          if (group.subGroups && group.subGroups.length > 0) {
            return {
              ...group,
              subGroups: updateGroup(group.subGroups),
            };
          }

          return group;
        });
      };

      state.groups = updateGroup(state.groups);
    },
    addNewGroup: (state, action: PayloadAction<TicketGroup>) => {
      const newGroup = action.payload;
      state.groups.push({ ...newGroup, ticketsList: [], subGroups: [] });
    },
    deleteGroup: (state, action: PayloadAction<string>) => {
      state.groups = state.groups.filter((g) => g._id !== action.payload);
    },
    updateGroupsOrder: (
      state,
      action: PayloadAction<{ groups: TicketGroup[] }>,
    ) => {
      const { groups } = action.payload;

      const orderMap = new Map(
        groups.map((g, i) => [JSON.stringify(g._id), i]),
      );

      state.groups = state.groups
        .map((group) => {
          const newOrder = orderMap.get(JSON.stringify(group._id));
          return newOrder !== undefined ? { ...group, order: newOrder } : group;
        })
        .sort((a, b) => a.order - b.order);
    },

    addTicketToGroup: (state, action) => {
      const ticket = action.payload;
      const group = state.groups.find(
        (item) => item?._id === ticket.status?._id,
      );
      if (group) {
        group.tickets.push(ticket);
      }
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    toggleTicketTags(
      state,
      action: PayloadAction<{
        ticketIds: string | string[];
        tagIds: string[];
      }>,
    ) {
      const { ticketIds, tagIds } = action.payload;
      const idSet = new Set(Array.isArray(ticketIds) ? ticketIds : [ticketIds]);

      state.groups = toggleTagsInGroups(state.groups, idSet, tagIds);
    },
    updateTicketFieldLocally(
      state,
      action: PayloadAction<{
        ticketIds: string | string[];
        updates: Partial<ITickets>;
      }>,
    ) {
      const { ticketIds, updates } = action.payload;
      const idSet = new Set(Array.isArray(ticketIds) ? ticketIds : [ticketIds]);

      state.groups = updateGroups(state.groups, idSet, updates);
    },

    updateTicketFieldAndShift(
      state,
      action: PayloadAction<{
        ticketIds: string | string[];
        updates: Partial<ITickets>;
        groupBy: GroupByTypeEnum;
        subGroupBy?: GroupByTypeEnum | null;
      }>,
    ) {
      const { ticketIds, updates, groupBy, subGroupBy } = action.payload;
      const ids = Array.isArray(ticketIds) ? ticketIds : [ticketIds];
      const idSet = new Set(ids);

      state.groups = updateGroups(state.groups, idSet, updates);

      const groupByValue = groupBy && updates[groupBy as keyof ITickets];
      debugger;
      if (groupBy && groupBy !== GroupByTypeEnum.NONE && groupByValue) {
        const targetGroupId = groupByValue as string;

        for (const ticketId of ids) {
          let sourceGroup: any = null;
          let movedTicket: any = null;
          let sourceIdx = -1;

          for (const group of state.groups) {
            const idx = group.ticketsList.findIndex(
              (t: any) => t._id === ticketId,
            );
            if (idx !== -1) {
              sourceGroup = group;
              sourceIdx = idx;
              movedTicket = group.ticketsList[idx];
              break;
            }
            for (const sub of group.subGroups ?? []) {
              const subIdx = sub.ticketsList.findIndex(
                (t: any) => t._id === ticketId,
              );
              if (subIdx !== -1) {
                sourceGroup = sub;
                sourceIdx = subIdx;
                movedTicket = sub.ticketsList[subIdx];
                break;
              }
            }
            if (movedTicket) break;
          }

          if (!movedTicket || !sourceGroup) continue;

          if (sourceGroup._id === targetGroupId) continue;

          const targetGroup = state.groups.find(
            (g: any) => g._id === targetGroupId,
          );
          if (!targetGroup) continue;

          sourceGroup.ticketsList.splice(sourceIdx, 1);
          const idIdx = sourceGroup.tickets?.indexOf(ticketId) ?? -1;
          if (idIdx !== -1) sourceGroup.tickets.splice(idIdx, 1);

          targetGroup.ticketsList.push(movedTicket);
          targetGroup.tickets.push(ticketId);
        }
      }

      const subGroupByValue =
        subGroupBy && updates[subGroupBy as keyof ITickets];

      if (
        subGroupBy &&
        subGroupBy !== GroupByTypeEnum.NONE &&
        subGroupByValue
      ) {
        const targetSubGroupId = subGroupByValue as string;

        for (const ticketId of ids) {
          for (const group of state.groups) {
            let found = false;

            for (const sub of group.subGroups ?? []) {
              const idx = sub.ticketsList.findIndex(
                (t: any) => t._id === ticketId,
              );
              if (idx === -1) continue;

              if (sub._id === targetSubGroupId) {
                found = true;
                break;
              }

              const movedTicket = sub.ticketsList.splice(idx, 1)[0];
              const idIdx = sub.tickets?.indexOf(ticketId) ?? -1;
              if (idIdx !== -1) sub.tickets.splice(idIdx, 1);

              const targetSub = group.subGroups?.find(
                (s: any) => s._id === targetSubGroupId,
              );
              if (targetSub) {
                targetSub.ticketsList.push(movedTicket);
                targetSub.tickets.push(ticketId);
              }

              found = true;
              break;
            }
            if (found) break;
          }
        }
      }
    },

    removeTicketLocally(
      state,
      action: PayloadAction<{
        ticketIds: string | string[];
      }>,
    ) {
      const { ticketIds } = action.payload;

      const idSet = new Set(Array.isArray(ticketIds) ? ticketIds : [ticketIds]);

      state.groups = state.groups.map((group) => ({
        ...group,
        ticketsList: group.ticketsList.filter(
          (ticket) => !idSet.has(ticket._id),
        ),
      }));
    },

    duplicateTicketLocally(
      state,
      action: PayloadAction<{
        ticketIds: string | string[];
      }>,
    ) {
      const { ticketIds } = action.payload;

      const ids = Array.isArray(ticketIds) ? ticketIds : [ticketIds];

      const idSet = new Set(ids);

      state.groups = state.groups.map((group) => {
        const newTickets: ITickets[] = []; // ✅ IMPORTANT FIX

        group.ticketsList.forEach((ticket) => {
          newTickets.push({ ...ticket });

          if (idSet.has(ticket._id)) {
            const duplicated: ITickets = {
              ...ticket,
              _id: `${ticket._id}_copy_${Date.now()}`,
              ticketID: `${ticket.ticketID}-COPY`,
              createdDate: new Date().toISOString(),
            };

            newTickets.push(duplicated);
          }
        });

        return {
          ...group,
          ticketsList: newTickets,
        };
      });
    },

    moveTicket: (
      state,
      action: PayloadAction<{
        ticketId: string;
        sourceGroupId: string;
        targetGroupId: string;
        sourceSubGroupId: string | null;
        targetSubGroupId: string | null;
        sourceIndex: number;
        destinationIndex: number;
        groupBy: GroupByTypeEnum;
        subGroupBy: GroupByTypeEnum | null;
      }>,
    ) => {
      const {
        ticketId,
        sourceGroupId,
        targetGroupId,
        sourceSubGroupId,
        targetSubGroupId,
        destinationIndex,
        groupBy,
        subGroupBy,
      } = action.payload;

      const sourceGroup = state.groups.find((g) => {
        if (Array.isArray(g._id)) {
          let parts;
          if (Array.isArray(sourceGroupId)) {
            parts = sourceGroupId;
          } else {
            parts = sourceGroupId.split(",").filter(Boolean);
          }
          return (
            g._id.length === parts.length &&
            parts.every((p) => g._id.includes(p))
          );
        }
        return (g._id as string) === sourceGroupId;
      });
      const targetGroup = state.groups.find((g) => {
        if (Array.isArray(g._id)) {
          let parts;
          if (Array.isArray(targetGroupId)) {
            parts = targetGroupId;
          } else {
            parts = targetGroupId.split(",").filter(Boolean);
          }
          return (
            g._id.length === parts.length &&
            parts.every((p) => g._id.includes(p))
          );
        }
        return (g._id as string) === targetGroupId;
      });
      if (!sourceGroup || !targetGroup) return;

      const sourceList =
        sourceSubGroupId === null
          ? sourceGroup
          : sourceGroup.subGroups?.find((s) => {
              if (Array.isArray(s._id)) {
                const parts = sourceSubGroupId
                  .split(",")
                  .sort()
                  .filter(Boolean);
                return (
                  s._id.length === parts.length &&
                  parts.every((p) => s._id.includes(p))
                );
              }
              return (s._id as string) === sourceSubGroupId;
            });

      const targetList =
        targetSubGroupId === null
          ? targetGroup
          : targetGroup.subGroups?.find((s) => {
              if (Array.isArray(s._id)) {
                const parts = targetSubGroupId
                  .split(",")
                  .sort()
                  .filter(Boolean);
                return (
                  s._id.length === parts.length &&
                  parts.every((p) => s._id.includes(p))
                );
              }
              return (s._id as string) === targetSubGroupId;
            });

      if (!sourceList?.ticketsList || !targetList) return;

      const actualIdx = sourceList.ticketsList.findIndex(
        (t) => t._id === ticketId,
      );

      if (actualIdx === -1) return;

      // ── Same group + same subgroup → only reorder ──
      if (
        sourceGroupId === targetGroupId &&
        sourceSubGroupId === targetSubGroupId
      ) {
        const [moved] = sourceList.ticketsList.splice(actualIdx, 1);
        sourceList.ticketsList.splice(destinationIndex, 0, moved);

        const idIdx = sourceList.tickets?.indexOf(ticketId) ?? -1;
        if (idIdx !== -1) {
          sourceList.tickets.splice(idIdx, 1);
          sourceList.tickets.splice(destinationIndex, 0, ticketId);
        }
        return;
      }

      // ── Cross group or cross subgroup → move ──
      const [moved] = sourceList.ticketsList.splice(actualIdx, 1);
      const idIdx = sourceList.tickets?.indexOf(ticketId) ?? -1;
      if (idIdx !== -1) sourceList.tickets.splice(idIdx, 1);
      if (!moved) return;

      // ✅ groupBy field update (on status change)
      if (
        groupBy &&
        groupBy !== GroupByTypeEnum.NONE &&
        sourceGroupId !== targetGroupId
      ) {
        (moved as any)[groupBy] = targetGroupId;
      }

      // ✅ subGroupBy field update (on subgroup change)
      if (
        subGroupBy &&
        subGroupBy !== GroupByTypeEnum.NONE &&
        sourceSubGroupId !== targetSubGroupId
      ) {
        (moved as any)[subGroupBy] = targetSubGroupId;
      }

      if (!targetList.ticketsList) targetList.ticketsList = [];
      if (!targetList.tickets) targetList.tickets = [];

      targetList.ticketsList.splice(destinationIndex, 0, moved);
      targetList.tickets.splice(destinationIndex, 0, ticketId);
    },

    moveMultipleTickets: (
      state,
      action: PayloadAction<{
        ticketIds: string[];
        sourceGroupId: string;
        targetGroupId: string;
        sourceSubGroupId: string | null;
        targetSubGroupId: string | null;
        destinationIndex: number;
        groupBy: GroupByTypeEnum;
        subGroupBy: GroupByTypeEnum | null;
      }>,
    ) => {
      const {
        ticketIds,
        sourceGroupId,
        targetGroupId,
        sourceSubGroupId,
        targetSubGroupId,
        destinationIndex,
        groupBy,
        subGroupBy,
      } = action.payload;

      const movedTickets: { ticket: any; ticketId: string }[] = [];

      for (const ticketId of ticketIds) {
        const sourceGroup = state.groups.find((g) => {
          if (Array.isArray(g._id)) {
            let parts;
            if (Array.isArray(sourceGroupId)) {
              parts = sourceGroupId;
            } else {
              parts = sourceGroupId.split(",").filter(Boolean);
            }
            return (
              g._id.length === parts.length &&
              parts.every((p) => g._id.includes(p))
            );
          }
          return (g._id as string) === sourceGroupId;
        });
        if (!sourceGroup) continue;

        const sourceList =
          sourceSubGroupId === null
            ? sourceGroup
            : sourceGroup.subGroups?.find((s) => {
                if (Array.isArray(s._id)) {
                  const parts = sourceSubGroupId
                    .split(",")
                    .sort()
                    .filter(Boolean);
                  return (
                    s._id.length === parts.length &&
                    parts.every((p) => s._id.includes(p))
                  );
                }
                return (s._id as string) === sourceSubGroupId;
              });

        if (!sourceList?.ticketsList) continue;

        const idx = sourceList.ticketsList.findIndex((t) => t._id === ticketId);
        if (idx === -1) continue;

        const [ticket] = sourceList.ticketsList.splice(idx, 1);

        const idIdx = sourceList.tickets?.indexOf(ticketId) ?? -1;
        if (idIdx !== -1) sourceList.tickets.splice(idIdx, 1);

        // ✅ groupBy field update
        if (
          groupBy &&
          groupBy !== GroupByTypeEnum.NONE &&
          sourceGroupId !== targetGroupId
        ) {
          (ticket as any)[groupBy] = targetGroupId;
        }

        // ✅ subGroupBy field update
        if (
          subGroupBy &&
          subGroupBy !== GroupByTypeEnum.NONE &&
          sourceSubGroupId !== targetSubGroupId
        ) {
          (ticket as any)[subGroupBy] = targetSubGroupId;
        }

        movedTickets.push({ ticket, ticketId });
      }

      if (movedTickets.length === 0) return;

      const targetGroup = state.groups.find((g) => {
        if (Array.isArray(g._id)) {
          let parts;
          if (Array.isArray(targetGroupId)) {
            parts = targetGroupId;
          } else {
            parts = targetGroupId.split(",").filter(Boolean);
          }
          return (
            g._id.length === parts.length &&
            parts.every((p) => g._id.includes(p))
          );
        }
        return (g._id as string) === targetGroupId;
      });
      if (!targetGroup) return;

      const targetList =
        targetSubGroupId === null
          ? targetGroup
          : targetGroup.subGroups?.find((s) => {
              if (Array.isArray(s._id)) {
                const parts = targetSubGroupId
                  .split(",")
                  .sort()
                  .filter(Boolean);
                return (
                  s._id.length === parts.length &&
                  parts.every((p) => s._id.includes(p))
                );
              }
              return (s._id as string) === targetSubGroupId;
            });

      if (!targetList) return;
      if (!targetList.ticketsList) targetList.ticketsList = [];
      if (!targetList.tickets) targetList.tickets = [];

      movedTickets.forEach(({ ticket, ticketId }, i) => {
        targetList.ticketsList.splice(destinationIndex + i, 0, ticket);
        targetList.tickets.splice(destinationIndex + i, 0, ticketId);
      });
    },

    setGroupFormValues: (
      state,
      action: PayloadAction<{ name: string; value: string }>,
    ) => {
      state.groupForm[action.payload.name].value = action.payload.value;
    },

    createTicketGroup: (state) => {
      state.isLoading = true;
    },
    addNewCustomGroup: (state, action: PayloadAction<TicketGroup>) => {
      const newGroup = action.payload;
      state.groups.push({ ...newGroup, ticketsList: [], subGroups: [] });
    },

    setTicketFormValue: (
      state,
      action: PayloadAction<{ name: string; value: any }>,
    ) => {
      set(state, `ticketFormData.${action.payload.name}`, action.payload.value);
    },
    setTicketTagsFormValue: (
      state,
      action: PayloadAction<{
        ids: string | string[];
      }>,
    ) => {
      const { ids } = action.payload;
      const tagIds = state.ticketFormData.tags ?? [];

      const incomingIds = Array.isArray(ids) ? ids : [ids];

      const allAlreadySelected = incomingIds.every((id) => tagIds.includes(id));

      if (allAlreadySelected) {
        state.ticketFormData.tags = tagIds.filter(
          (id) => !incomingIds.includes(id),
        );
      } else {
        const toAdd = incomingIds.filter((id) => !tagIds.includes(id));
        state.ticketFormData.tags = [...tagIds, ...toAdd];
      }
    },
    setTicketFormCustomField: (
      state,
      action: PayloadAction<{
        fieldId: string;
        error?: string;
        value: string | number | boolean | Date | string[];
      }>,
    ) => {
      const { fieldId, error, value } = action.payload;
      const existingFieldIndex = state.ticketFormData.customFields.findIndex(
        (field) => field.fieldId === fieldId,
      );

      if (existingFieldIndex !== -1) {
        // Update existing custom field
        state.ticketFormData.customFields[existingFieldIndex] = {
          fieldId,
          error,
          value,
        };
      } else {
        // Add new custom field
        state.ticketFormData.customFields.push({
          fieldId,
          error,
          value,
        });
      }
    },
    setTicketAttachments: (
      state,
      action: PayloadAction<{
        data: CreateAssetResponse;
      }>,
    ) => {
      const data = action.payload.data;
      state.ticketFormData.attachments.push(data._id);
      state.ticketFormData.file.push(data);
    },
    setCreateTicket: (
      state,
      action: PayloadAction<{
        projectId: string;
        boardId: string;
        groupId: string;
        callback?: () => void;
      }>,
    ) => {
      state.isLoading = true;
    },
    clearCreateTicketForm: (state) => {
      state.isLoading = false;
      const { status, type, groupId } = state.ticketFormData;
      state.ticketFormData = {
        ...defaultTicketFormDetail,
        status,
        type,
        groupId,
      };
    },
    clearCreateTicket: (state) => {
      state.isLoading = false;
      state.ticketFormData = {
        ...defaultTicketFormDetail,
      };
    },
    removeTicketFromGroup: (state, action: PayloadAction<ISelectedTickets>) => {
      const { group, id } = action.payload;
      state.groups = state.groups.map((ele) => {
        // If the group is in the list of groups to update
        if (group.includes(ele._id)) {
          return {
            ...ele,
            tickets: ele.tickets.filter((ticket) => !id.includes(ticket)),
            ticketsList: ele.ticketsList.filter(
              (ticket) => !id.includes(ticket._id),
            ),
          };
        }
        return ele;
      });
    },

    // fetch grouping reducer

    setGroupingLoader: (state, action: PayloadAction<boolean>) => {
      state.groupingLoader = action.payload;
    },
    setGroupingTicketsLoader: (state, action: PayloadAction<boolean>) => {
      state.groupingTicketsLoader = action.payload;
    },
    fetchBoardsGroupsJson: (
      state,
      action: PayloadAction<IGroupJsonPayload>,
    ) => {
      state.groupingLoader = true;
    },

    setBoardGroupsJson: (state, action: PayloadAction<TicketGroup[]>) => {
      state.groups = action.payload;
    },
    updateTicketCache: (state, action: PayloadAction<Record<string, any>>) => {
      state.ticketCache = { ...state.ticketCache, ...action.payload };
    },
    setGroupsPagination: (
      state,
      action: PayloadAction<Record<string, any>>,
    ) => {
      state.groupsPagination = action.payload;
    },
    loadMoreGroupTickets: (
      state,
      action: PayloadAction<{
        groupId: string;
        batchSize?: number;
        callback?: () => void;
      }>,
    ) => {},
    fetchGroupInitialTickets: (
      state,
      action: PayloadAction<{
        groupId: string;
        batchSize?: number;
        callback?: () => void;
      }>,
    ) => {},
  },
});

export const {
  actions: ticketGroupsActions,
  reducer: ticketGroupsReducer,
  name: ticketGroupsSlice,
} = formSlice;
export { initialState };
